import { ListKeyManager } from '@angular/cdk/a11y';
import mixinDisabled from '../../mixins/disabled';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
  forwardRef,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  Optional,
  SkipSelf,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
  AfterViewInit,
  Renderer2,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { debounce, humanizeString, escapeRegExp, isCoodinatesInput } from '../../utils';
import { PHOENIX_CONTEXTS, PhxContextService } from '../../services/phx-context-service';
import { KeyboardKeys } from '../../helpers/KeyboardKeys.enum';
import { PhxMenuItemComponent } from '../menu/phx-menu-item/phx-menu-item.component';

import { PhxSuggestions, PhxSuggestion, PhxSearchSuggestion, PhxTypeaheadSize, PhxDefaultItems } from './typeahead.types';

import { MagnifyingGlass, Enter, LocationPin } from '../icon/icon-map';

const MAX_SEARCH_HISTORY_LENGTH = 4;
const DEBOUNCE_TIMER = 300;
const SEARCH_HISTORY_STRING: string = 'typeaheadSearchHistory';

const baseTypeaheadMixin = mixinDisabled(
  class {
    constructor() {}
  }
);

@Component({
  selector: 'phx-typeahead',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhxTypeaheadComponent),
      multi: true,
    },
  ],
  inputs: ['phxDisabled'],
  host: {
    '[class]': 'getClass',
    '[class.phx-filter]': 'phxStyleType === PHOENIX_CONTEXTS.FILTER',
  },
  templateUrl: 'typeahead.component.html',
  styleUrls: ['./typeahead.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class PhxTypeaheadComponent extends baseTypeaheadMixin implements OnInit, OnChanges, AfterViewInit {
  @Input() public phxSuggestions: PhxSuggestions;
  @Input() public phxUserInput: string;
  @Input() public phxPlaceholder?: string;
  @Input() public phxType: string;
  @Input() public phxSize: PhxTypeaheadSize = 'medium';
  @Input() public phxNoResultsHeading?: string;
  @Input() public phxNoResultsMessage?: string;
  @Input() public phxHideSearchIndicator?: boolean = false;
  @Input() public phxLoading?: boolean = false;
  @Input() public phxUserInputEnterDisabled?: boolean = false;
  @Input() public phxDefaultOptions: { label: string; value: string }[] | null = null;
  @Input() public phxShowRecentSearchHistory?: boolean = true;
  @Input() public phxDropdownWidth: number | string = null;
  @Input() public phxDropdownHeight: number | string = null;
  @Output() public phxSuggestionSelected = new EventEmitter<PhxSuggestion | PhxSearchSuggestion | PhxDefaultItems>();
  @Output() public phxUserInputChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() public phxInputClear: EventEmitter<string> = new EventEmitter<string>();
  @ViewChild('dropdownRef', { read: ElementRef, static: false }) dropdownRef: ElementRef;
  @ViewChild('searchInputRef', { read: ElementRef, static: false }) searchInputRef: ElementRef;
  @ViewChildren(PhxMenuItemComponent) private menuItems!: QueryList<PhxMenuItemComponent>;

  showSuggestions: boolean = false;
  lastSearches: PhxSuggestion[] = [];
  listKeyManager: ListKeyManager<PhxMenuItemComponent>;

  public phxDropdownClass: string;
  public dropdownVisible: boolean = false;
  public filteredSuggestions: PhxSuggestion[] = [];
  public shouldShowNoResultsState: boolean = false;
  public readonly PHOENIX_CONTEXTS = PHOENIX_CONTEXTS;
  public phxStyleType: PHOENIX_CONTEXTS;
  public magnifyingGlassIcon = MagnifyingGlass;
  public enterIcon = Enter;
  public locationIcon = LocationPin;
  public dataLoaded: boolean = false;
  public isCoodinatesIcon = false;
  // TODO: Create a global localStorage service in PHX lib FP-1495
  // public showMessage: boolean = localStorage.getItem('firstMessageShown') === 'true';

  private searchHistoryKey: string;
  private hasPerformedSearch: boolean = false;
  private focusInsideComponent: boolean = false;
  private debounceFilterSuggestions = debounce(this.filterSuggestions.bind(this), DEBOUNCE_TIMER);
  private destroy$: Subject<void> = new Subject<void>();

  get getClass() {
    return `phx-typeahead phx-typeahead-${this.phxSize}`;
  }

  get dropdownClasses(): string {
    // Construct the base class for the dropdown
    let classes = `phx-typeahead-dropdown${!this.phxDropdownWidth ? ` phx-typeahead-dropdown-${this.phxSize}` : ''} ${this.phxDropdownClass}`;

    // Check if phxDropdownHeight is provided and add a corresponding height class
    if (this.phxDropdownHeight) {
      classes += ` phx-typeahead-dropdown-height-custom`;
    }

    return classes;
  }

  constructor(
    private _cdr: ChangeDetectorRef,
    private _sanitizer: DomSanitizer,
    private _renderer: Renderer2,
    @Optional() @SkipSelf() contextService: PhxContextService
  ) {
    super();
    this.phxStyleType = contextService?.getType();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { phxSuggestions, phxDefaultOptions, phxUserInput } = changes;

    if (!phxSuggestions?.firstChange && phxSuggestions) {
      this.updateFilteredSuggestions();
    }

    if (phxDefaultOptions && this.phxDefaultOptions?.length > 0) {
      this.phxShowRecentSearchHistory = false;
      this.phxHideSearchIndicator = true;
    }

    if (phxUserInput) {
      if (!this.phxUserInput) {
        this.phxUserInput = phxUserInput.currentValue;
      }
    }
  }

  ngOnInit(): void {
    this.searchHistoryKey = `${SEARCH_HISTORY_STRING}_${this.phxType}`;
    // Ensure defaultOptions is an empty array if not provided
    if (!this.phxDefaultOptions) {
      this.phxDefaultOptions = [];
    }
    this.loadSearchHistory();
  }

  ngAfterViewInit() {
    this.listKeyManager = new ListKeyManager(this.menuItems).withWrap().withTypeAhead();
    this.initKeyManagerHandlers();

    if (this.phxStyleType !== PHOENIX_CONTEXTS.FILTER) {
      this.phxSize = 'full-width';
      this._adjustDropdownWidth();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initKeyManagerHandlers(): void {
    this.listKeyManager
      .change
      .pipe(takeUntil(this.destroy$))
      .subscribe((activeIndex) => {
        this.menuItems.map((item, index) => {
          if ( activeIndex === index ) {
            this._renderer.addClass(item.elementRef.nativeElement, 'phx-typeahead-focused');
          } else {
            this._renderer.removeClass(item.elementRef.nativeElement, 'phx-typeahead-focused');
          }
          return item;
        });
      });
  }

  updateFilteredSuggestions(): void {
    const query = this.phxUserInput;
    this.filteredSuggestions = this.filterSuggestions(query, this.phxSuggestions);
    this._cdr.detectChanges();
  }

  onInput(event: Event): void {
    const query: string = (event.target as HTMLInputElement).value;

    // this.dropdownVisible = query.length > 0;

    if (query.length <= 1) {
      this.dataLoaded = false;
      this.phxSuggestions = null;
      this.shouldShowNoResultsState = false;
      this.filteredSuggestions = [];
      this.phxLoading = false;
      // this.dropdownVisible = false;
    }

    // TODO: comment out the 0 state for now
    // if (!localStorage.getItem('firstMessageShown')) {
    // this.showMessage = true;
    // TODO: Create a global localStorage service in PHX lib FP-1495
    // localStorage.setItem('firstMessageShown', 'true');
    // }

    this.debounceFilterSuggestions(query, this.phxSuggestions);
    this.showSuggestions = true;
    this.isCoodinatesIcon = isCoodinatesInput(query);
    this.phxUserInputChange.emit(query);
    this._cdr.detectChanges();
  }

  // TODO: cleanup old filterSuggestions method
  // filterSuggestions(query: string, suggestions: PhxSuggestions): PhxSuggestion[] {
  //   if (!suggestions) {
  //     return [];
  //   }

  //   const filteredSuggestions: PhxSuggestion[] = [];

  //   // Escape the query string to treat special characters literally
  //   const escapedQueryString = escapeRegExp(query);

  //   // Construct the regular expression
  //   // const queryRegex = new RegExp(`^${escapedQueryString.replace(/,/g, '\\s*,\\s*')}`, 'i');
  //   const queryRegex = new RegExp(escapedQueryString, 'i');

  //   Object.entries(suggestions).forEach(([key, items]) => {
  //     this.dataLoaded = true;
  //     if (items && items.length > 0) {
  //       const filteredItems = items.filter((item) => {
  //         // For other keys, only check name and description
  //         if (key === 'landDescription') {
  //           return queryRegex.test((item.id && item.id.toString()) || '');
  //         } else {
  //           return queryRegex.test(item.name) || (item.description && queryRegex.test(item.description));
  //         }
  //       });

  //       // Exclude the entire category if there are no matching items
  //       if (filteredItems.length > 0) {
  //         // Add separator for the category
  //         filteredSuggestions.push({ separator: true, name: humanizeString(key) } as PhxSuggestion);

  //         // Add filtered items to the suggestions
  //         filteredSuggestions.push(...filteredItems.map((item) => ({ ...item } as PhxSuggestion)));
  //       }
  //     }
  //   });

  //   this.shouldShowNoResultsState = filteredSuggestions.length === 0;

  //   return filteredSuggestions;
  // }

  filterSuggestions(query: string, suggestions: PhxSuggestions): PhxSuggestion[] {
    if (!suggestions) {
      return [];
    }

    const transformedSuggestions: PhxSuggestion[] = [];

    // Loop through the suggestions and transform them as needed
    Object.entries(suggestions).forEach(([key, items]) => {
      this.dataLoaded = true;
      if (items && items.length > 0) {
        // Apply your own transformation to each item
        const transformedItems = items.map((item) => ({
          ...item,  // Keep the item properties as is, or transform if needed
        }));

        // Add separator for the category
        transformedSuggestions.push({ separator: true, name: humanizeString(key) } as PhxSuggestion);

        // Add transformed items to the suggestions
        transformedSuggestions.push(...transformedItems.map((item) => ({ ...item } as PhxSuggestion)));
      }
    });

    // Update state for showing "no results" if needed
    this.shouldShowNoResultsState = transformedSuggestions.length === 0;

    return transformedSuggestions;
  }

  highlightMatches(item: PhxSuggestion, query: string): SafeHtml {
    if (!item || !query) return '';
    const text = item.name || item.description || (item.id && item.id.toString()) || '';
    const regex = new RegExp(escapeRegExp(query), 'gi');
    const sanitizedText = text.replace(regex, (match) => `<strong class="highlighted">${match}</strong>`);
    return this._sanitizer.bypassSecurityTrustHtml(sanitizedText);
  }

  highlightSubTextMatches(item: PhxSuggestion, query: string): SafeHtml {
    if (!item || !query) return '';
    const text = item.subtext || item.description || '';
    const regex = new RegExp(escapeRegExp(query), 'gi');
    const sanitizedText = text.replace(regex, (match) => `<strong class="highlighted">${match}</strong>`);
    return this._sanitizer.bypassSecurityTrustHtml(sanitizedText);
  }

  selectSuggestion(suggestion: PhxSuggestion): void {
    this.phxUserInput = suggestion?.name || suggestion?.description || suggestion?.id.toString();
    this.showSuggestions = this.filteredSuggestions.length > 0;
    this.dropdownVisible = false;
    this.phxLoading = false;
    this.listKeyManager.setActiveItem(-1);
    this.phxUserInputChange.emit(this.phxUserInput);
    this.phxSuggestionSelected.emit(suggestion);
    this.recordLastSearch(suggestion);
  }

  // Method to handle selecting a default option
  selectDefaultOption(option: PhxDefaultItems) {
    this.phxUserInput = option.value;
    this.dropdownVisible = false;
    this.phxLoading = false;
    this.listKeyManager.setActiveItem(-1);
    this.phxUserInputChange.emit(this.phxUserInput);
    this.phxSuggestionSelected.emit(option);
  }

  recordLastSearch(search: PhxSuggestion): void {
    try {
      // Check if there is existing search history for this instance
      let instanceSearchHistory = JSON.parse(localStorage.getItem(this.searchHistoryKey) || '[]');
      const resultExists = instanceSearchHistory.some((existingSearch: PhxSuggestion) => {
        switch (search.type) {
          case 'landDescription':
            return existingSearch.id && existingSearch.id.toString() === search.id.toString();
          case 'location':
            return existingSearch.description === search.description;
          default:
            return existingSearch.id && existingSearch.id === search.id;
        }
      });

      // Check if any search with the same id already exists
      if (!resultExists) {
        instanceSearchHistory.unshift(search);

        if (instanceSearchHistory.length > MAX_SEARCH_HISTORY_LENGTH) {
          instanceSearchHistory.pop();
        }

        localStorage.setItem(this.searchHistoryKey, JSON.stringify(instanceSearchHistory));
      }

      this.loadSearchHistory();
      this._cdr.detectChanges();
    } catch (error) {
      console.error('Error recording search history:', error);
    }
  }

  loadSearchHistory(): void {
    const savedHistory = localStorage.getItem(this.searchHistoryKey);
    if (savedHistory) {
      this.lastSearches = JSON.parse(savedHistory);
    }
  }

  searchString(): void {
    this.hasPerformedSearch = true;
    this.dropdownVisible = false;
    this.listKeyManager.setActiveItem(-1);
    this.phxUserInputChange.emit(this.phxUserInput);
    this.phxSuggestionSelected.emit({ value: this.phxUserInput, type: 'stringSearch' });
  }

  onClearClick(): void {
    this.searchInputRef.nativeElement.querySelector('input').focus();
    this.phxUserInputChange.emit('');
    this.phxInputClear.emit();
    this.phxUserInput = '';
    this.showSuggestions = false;
    this.dataLoaded = false;
    this.shouldShowNoResultsState = false;
    this.phxLoading = false;
    this.listKeyManager.setActiveItem(-1);

    if (this.hasPerformedSearch) {
      this.phxSuggestionSelected.emit({ value: '', type: 'stringSearch' });
    }

    this._cdr.detectChanges();
  }

  public onKeyDown(event: KeyboardEvent) {
		if (!this.dropdownVisible) {
			return;
		}

		event.stopImmediatePropagation();
    if (this.listKeyManager) {
      if (event.key === KeyboardKeys.ARROW_DOWN || event.key === KeyboardKeys.ARROW_UP) {
        // passing the event to key manager so we get a change fired
        this.listKeyManager.onKeydown(event);
        if (this.listKeyManager.activeItem) {
          this.listKeyManager.activeItem.elementRef.nativeElement.scrollIntoView({ block: 'nearest' });
        }
        return false;
      } else if (event.key === KeyboardKeys.ENTER) {
        // when we hit enter, the keyboardManager should call the selectItem method of the `ListItemComponent`
        this.listKeyManager.onKeydown(event);
        if (this.listKeyManager.activeItem) {
          const activeItem = this.listKeyManager.activeItem;
          if (activeItem) {
            const mouseEvent = new MouseEvent('mousedown', {
              view: window,
              bubbles: true,
              cancelable: true
            });
            activeItem.elementRef.nativeElement.focus();
            activeItem.elementRef.nativeElement.dispatchEvent(mouseEvent);
          }
        } else if (!this.phxUserInputEnterDisabled) {
          this.searchString();
        }
        return false;
      }
    }
	}

  public trackByFn(index: number, item: any): any {
    return item.id || index; // adjust based on your data structure
  }

  private _adjustDropdownWidth(): void {
    const inputWidth = this.searchInputRef.nativeElement.offsetWidth;

    if (this.dropdownRef && this.dropdownRef.nativeElement) {
      this.phxDropdownWidth = inputWidth >= 240 ? inputWidth : 240;
    }
  }

  @HostListener('focusin')
  onFocusIn(): void {
    this._adjustDropdownWidth();
    this.focusInsideComponent = true;

    // Show dropdown if any of these conditions are true:
    // 1. Input has 2+ characters
    // 2. There are default options
    // 3. There are search history items and no input
    // const hasValidInput = this.phxUserInput && this.phxUserInput.length > 1;
    // const hasDefaultOptions = this.phxDefaultOptions?.length > 0;
    // const hasSearchHistory = this.lastSearches?.length > 0 && !this.phxUserInput;

    // if (hasValidInput || hasDefaultOptions || hasSearchHistory) {
    this.dropdownVisible = true;
    // }
  }

  @HostListener('focusout', ['$event'])
  onFocusOut(event: FocusEvent): void {
    const relatedTarget = event.relatedTarget as HTMLElement;

    // Check if dropdownRef exists before accessing its nativeElement property
    if (!relatedTarget || !this.dropdownRef.nativeElement.contains(relatedTarget)) {
      this.focusInsideComponent = false;

      setTimeout(() => {
        if (!this.focusInsideComponent) {
          this.dropdownVisible = false;  // Close the dropdown if focus is truly outside
          this._cdr.detectChanges();
        }
      }, 0);
    }
  }

  @HostListener('document:click', ['$event.target'])
  onClickOutside(target: HTMLElement) {
    if (!this.dropdownRef.nativeElement.contains(target) &&
        !this.searchInputRef.nativeElement.contains(target)) {
      this.dropdownVisible = false;  // Close the dropdown if clicking outside
    }
  }
}
